import { FocusMonitor } from '@angular/cdk/a11y';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  ViewChild,
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, UntypedFormBuilder, UntypedFormGroup, NgControl, ValidatorFn } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { takeWhile, tap } from 'rxjs/operators';
import { DialogService } from 'src/app/order/services/internal/dialog/dialog.service';
import { FdFieldBaseComponent } from '../fd-field/fd-field.base-component';
import { UpperCasePipe, LowerCasePipe } from '@angular/common';
import { MaskConfig, MaskPatterns } from '../mask.types';

@Component({
    selector: 'fd-input',
    templateUrl: 'fd-input.component.html',
    styleUrls: ['fd-input.component.scss'],
    host: {
        '[class.example-floating]': 'shouldLabelFloat',
        '[id]': 'id',
        '[attr.aria-describedby]': 'describedBy',
    },
    standalone: false
})
export class FdInputComponent extends FdFieldBaseComponent implements ControlValueAccessor, OnInit, OnDestroy {
  [x: string]: any;
  public id = `fd-input-${FdInputComponent.nextId++}`;

  get resolvedInputId(): string {
    return `${this.id}-${this.field?.controlName || 'control'}`;
  }

  @HostBinding('class.fd-field--invalid')
  get invalid(): boolean {
    this.cdr.detectChanges();
    return this.hasError;
  }

  get relatedFormControl(): AbstractControl {
    return this.parentForm.get(this.field.controlName);
  }

  get hasError() {
    return this.relatedFormControl && this.relatedFormControl.errors !== null;
  }

  get errorMessages() {
    return Object.keys(this.field.messages)
      .filter((val) => this.relatedFormControl.errors[val])
      .map((key) => this.field.messages[key]);
  }

  get shouldLabelFloat() {
    return this.focused;
  }

  @Input()
  get placeholder(): string {
    return this._placeholder;
  }
  set placeholder(value: string) {
    this._placeholder = value;
    this.stateChanges.next();
  }

  get required(): boolean {
    return (
      this.relatedFormControl &&
      this.relatedFormControl.validator &&
      this.relatedFormControl.validator(this.relatedFormControl) !== null &&
      this.relatedFormControl.validator(this.relatedFormControl).hasOwnProperty(this.REQUIRED_VALIDATOR_KEY)
    );
  }

  get hasIcon(): boolean {
    return this.field && !!this.field.iconHTML;
  }
  public static nextId = 0;
  public alive = true;

  private whitespaceValidator: ValidatorFn = (control: AbstractControl) => {
    if (!control.value) {
      return null;
    }
    const isWhitespace = (control.value || '').trim().length === 0;
    return isWhitespace ? { whitespace: true } : null;
  };

  @ViewChild('tooltip') public tooltip: MatTooltip;

  @Input()
  public field: FdInputConfig;

  @Output()
  public input = new EventEmitter<string>();

  @Input()
  public parentForm: UntypedFormGroup;

  @Output()
  public blur = new EventEmitter<string>();

  @Output()
  pasteHandler = new EventEmitter<string>();

  public stateChanges = new Subject<void>();
  public focused = false;
  public errorState = false;
  public controlType = 'fd-input';
  public describedBy = '';
  public maskExpression = '';
  public resolvedPatterns?: MaskPatterns;
  private readonly REQUIRED_VALIDATOR_KEY = 'required';
  private _placeholder: string;

  constructor(
    formBuilder: UntypedFormBuilder,
    private sanitizer: DomSanitizer,
    private focusMonitor: FocusMonitor,
    private upperCasePipe: UpperCasePipe,
    private lowerCasePipe: LowerCasePipe,
    private cdr: ChangeDetectorRef,
    private dialogService: DialogService,
    private elementRef: ElementRef<HTMLElement>,
    @Optional() @Self() public ngControl: NgControl
  ) {
    super();

    focusMonitor
      .monitor(elementRef, true)
      .pipe(takeWhile(() => this.alive))
      .subscribe((origin) => {
        if (this.focused && !origin) {
          this.onTouched();
        }
        this.focused = !!origin;
        this.stateChanges.next();
      });

    if (this.ngControl !== null) {
      this.ngControl.valueAccessor = this;
    }
  }
  public onChange = (_: any) => {};
  public onTouched = () => {};

  public showTooltipAndStopPropagation($event: any) {
    this.tooltip.toggle();
    event.stopImmediatePropagation();
  }

  public unmask(control: AbstractControl, maskCharsReplace: RegExp): string {
    let unmaskedValue = '';
    if (!control) {
      return;
    }
    control.valueChanges
      .pipe(takeWhile(() => this.alive))
      .pipe(
        tap((value: string = '') => {
          if (value) {
            unmaskedValue = value.replace(maskCharsReplace, '').trim();
            control.setValue(unmaskedValue, {
              emitEvent: false,
              emitModelToViewChange: false,
            });
          }
        })
      )
      .pipe(takeWhile(() => this.alive))
      .subscribe();

    return unmaskedValue;
  }

  public ngOnDestroy() {
    this.alive = false;
    this.stateChanges.complete();
    this.focusMonitor.stopMonitoring(this.elementRef);
  }

  transformInput(control: AbstractControl, transformType: TransformType) {
    let transformPipe: UpperCasePipe | LowerCasePipe;
    if (transformType === TransformType.UPPERCASE) {
      transformPipe = this.upperCasePipe;
    } else if (transformType === TransformType.LOWERCASE) {
      transformPipe = this.lowerCasePipe;
    } else {
      return;
    }

    if (!control) {
      return;
    }
    control.valueChanges
      .pipe(takeWhile(() => this.alive))
      .pipe(
        tap((value: string = '') => {
          if (value) {
            control.setValue(transformPipe.transform(control.value), { emitEvent: false, emitModelToViewChange: false });
          }
        })
      )
      .subscribe();
  }

  public ngOnInit(): void {
    this.resolveMask();

    if (this.field && this.field.disabled) {
      this.relatedFormControl.disable();
    }

    if (this.field && this.field.mask) {
      this.unmask(this.relatedFormControl, this.field.maskCharsReplace);
    }

    if (this.field && this.field.iconHTML) {
    }

    if (this.field && this.field.transform) {
      this.transformInput(this.relatedFormControl, this.field.transform);
    }

    // Add whitespace validator if the field is required.
    if (this.required) {
      const currentValidators = this.relatedFormControl.validator ? [this.relatedFormControl.validator, this.whitespaceValidator] : [this.whitespaceValidator];
      this.relatedFormControl.setValidators(currentValidators);
      this.relatedFormControl.updateValueAndValidity();
    }

    if (this.relatedFormControl?.valueChanges && this.field?.mask) {
      this.relatedFormControl.valueChanges.pipe(takeWhile(() => this.alive)).subscribe((value) => {
        this.resolveMask(value);
      });
    }
  }

  private resolveMask(value?: string): void {
    if (typeof this.field?.mask === 'function') {
      this.maskExpression = this.field.mask(value ?? this.relatedFormControl?.value ?? '');
      this.resolvedPatterns = this.field.mask.patterns;
      return;
    }

    this.maskExpression = this.field?.mask || '';
    this.resolvedPatterns = undefined;
  }

  public setDescribedByIds(ids: string[]) {
    this.describedBy = ids.join(' ');
  }

  public onContainerClick(event: MouseEvent) {
    if ((event.target as Element).tagName.toLowerCase() !== 'input') {
      this.elementRef.nativeElement.querySelector('input')!.focus();
    }
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {}

  public handleBlur(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    let inputValue = inputElement.value;

    // Clear field if it contains only blank spaces.
    if (inputValue.trim().length === 0) {
      this.relatedFormControl.setValue('', { emitEvent: false });
      inputElement.value = '';
      inputValue = '';
    }

    if (this.blur) {
      if (this.field.isNumber) {
        this.blur.emit(inputValue.replace(/\D+/g, ''));
      }
      this.blur.emit(inputValue);
    }
    event.stopPropagation();
  }

  public handleChange(event: Event): void {
    const inputValue = (event.target as HTMLInputElement).value;
    this.resolveMask(inputValue);

    if (this.input) {
      this.input.emit(inputValue);
    }
    event.stopPropagation();
  }

  pasteHandle(event: ClipboardEvent) {
    if (this.pasteHandler?.observers?.length > 0) {
      event.preventDefault();
      const clipboardData = event.clipboardData || (window as any).clipboardData;
      let pastedText = clipboardData.getData('text');
      pastedText = pastedText.replace(/\s+/g, ' ').trim();
      this.pasteHandler.emit(pastedText);
    }
  }
}

export enum InputType {
  DATE = 'date',
  EMAIL = 'email',
  HIDDEN = 'hidden',
  IMAGE = 'image',
  MONTH = 'month',
  NUMBER = 'number',
  PASSWORD = 'password',
  RANGE = 'range',
  SEARCH = 'search',
  TEL = 'tel',
  TEXT = 'text',
  TIME = 'time',
  URL = 'url',
  WEEK = 'week',
}

export enum TransformType {
  UPPERCASE = 'UPPERCASE',
  LOWERCASE = 'LOWERCASE',
}

export interface FdInputConfig {
  controlName: string;
  label: string;
  tooltipMessage?: string;
  hint?: string;
  iconHTML?: SafeHtml;
  maxLength?: number;
  disabled?: boolean;
  type?: InputType;
  mask?: MaskConfig;
  maskCharsReplace?: RegExp;
  messages?: { [key: string]: string };
  error?: boolean;
  isNumber?: boolean;
  transform?: TransformType;
}
